import type { ManipulateType } from 'dayjs';
import {
  getNowInMeetingTimeZone,
  utcIsoToMeetingDateTime,
} from '@/modules/common/helpers/meetingDateTime.ts';
import { MeetingReminder, MeetingReminderUnit, ScheduledMeetingReminderItem } from 'compliance-api';

export interface MeetingReminderLike {
  value: number;
  unit: string;
}

export const MAX_MEETING_REMINDERS = 5;

export const defaultMeetingReminder: MeetingReminder = {
  value: 1,
  unit: MeetingReminderUnit.Hours,
};

export const normalizeMeetingReminderUnit = (unit: string): MeetingReminderUnit => {
  return Object.values(MeetingReminderUnit).includes(unit as MeetingReminderUnit)
    ? (unit as MeetingReminderUnit)
    : MeetingReminderUnit.Hours;
};

export const normalizeMeetingReminder = (
  reminder: ScheduledMeetingReminderItem,
): MeetingReminder => ({
  value: reminder.value,
  unit: normalizeMeetingReminderUnit(reminder.unit),
});

export const formatMeetingReminderLabel = (reminder: MeetingReminder): string => {
  const unitLabel = {
    [MeetingReminderUnit.Minutes]: 'minute',
    [MeetingReminderUnit.Hours]: 'hour',
    [MeetingReminderUnit.Weeks]: 'week',
    [MeetingReminderUnit.Months]: 'month',
  }[reminder.unit];

  const suffix = reminder.value === 1 ? unitLabel : `${unitLabel}s`;
  return `${reminder.value} ${suffix} before`;
};

export const getReminderKey = (reminder: MeetingReminderLike) => {
  return `${reminder.value}:${normalizeMeetingReminderUnit(reminder.unit)}`;
};

export const isReminderInPast = ({
  reminder,
  startTime,
  timeZone,
}: {
  reminder: MeetingReminderLike;
  startTime?: string;
  timeZone?: string | null;
}) => {
  if (!startTime) return false;

  const meetingStart = utcIsoToMeetingDateTime(startTime, timeZone);
  const reminderTime = meetingStart.subtract(reminder.value, reminder.unit as ManipulateType);
  return reminderTime.isBefore(getNowInMeetingTimeZone(timeZone));
};

export const getVisibleMeetingReminders = ({
  reminders = [],
  startTime,
  timeZone,
  isRecurring,
}: {
  reminders?: MeetingReminderLike[];
  startTime?: string;
  timeZone?: string | null;
  isRecurring: boolean;
}): MeetingReminder[] => {
  if (isRecurring) {
    return reminders.map(reminder => ({
      value: reminder.value,
      unit: normalizeMeetingReminderUnit(reminder.unit),
    }));
  }

  return reminders
    .filter(reminder => !isReminderInPast({ reminder, startTime, timeZone }))
    .map(reminder => ({
      value: reminder.value,
      unit: normalizeMeetingReminderUnit(reminder.unit),
    }));
};

export const getDuplicateReminderIndexes = (reminders: MeetingReminderLike[] = []) => {
  const seen = new Map<string, number>();
  const duplicateIndexes = new Set<number>();

  reminders.forEach((reminder, index) => {
    const key = getReminderKey(reminder);
    const previousIndex = seen.get(key);

    if (previousIndex !== undefined) {
      duplicateIndexes.add(previousIndex);
      duplicateIndexes.add(index);
      return;
    }

    seen.set(key, index);
  });

  return Array.from(duplicateIndexes).sort((a, b) => a - b);
};
